import type { TurnEvent } from "./types";

/**
 * Collapses per-content-block lines into billable turns. Usage is taken as a
 * per-field max across the group (lines repeat the same final usage object);
 * tool names and thinking/text lengths are aggregated across the group.
 */
export function mergeTurnLines(lines: TurnEvent[]): TurnEvent[] {
  const groups = new Map<string, TurnEvent[]>();
  const order: string[] = [];
  for (const line of lines) {
    const key = line.messageId ?? line.uuid;
    const group = groups.get(key);
    if (group) group.push(line);
    else {
      groups.set(key, [line]);
      order.push(key);
    }
  }
  return order.map((key) => {
    const group = groups.get(key) as TurnEvent[];
    const first = group[0] as TurnEvent;
    if (group.length === 1) return first;
    const timestamps = group.map((l) => l.timestamp).filter((t) => t > 0);
    return {
      ...first,
      usage: {
        input: Math.max(...group.map((l) => l.usage.input)),
        output: Math.max(...group.map((l) => l.usage.output)),
        cacheRead: Math.max(...group.map((l) => l.usage.cacheRead)),
        cacheWrite: Math.max(...group.map((l) => l.usage.cacheWrite)),
        cacheWrite5m: Math.max(...group.map((l) => l.usage.cacheWrite5m)),
        cacheWrite1h: Math.max(...group.map((l) => l.usage.cacheWrite1h)),
      },
      tools: group.flatMap((l) => l.tools),
      numToolUses: group.reduce((n, l) => n + l.numToolUses, 0),
      thinkingChars: group.reduce((n, l) => n + l.thinkingChars, 0),
      textChars: group.reduce((n, l) => n + l.textChars, 0),
      timestamp: timestamps.length > 0 ? Math.min(...timestamps) : first.timestamp,
      stopReason: [...group].reverse().find((l) => l.stopReason)?.stopReason,
      speed: group.find((l) => l.speed)?.speed,
      inferenceGeo: group.find((l) => l.inferenceGeo)?.inferenceGeo,
    };
  });
}
